import React, { useEffect, useMemo, useState } from 'react';
import BoletaFinalPreview from './BoletaFinalPreview';

// Constante `PUBLIC_URL` que centraliza un valor de configuración compartido.
const PUBLIC_URL = process.env.PUBLIC_URL || '';

// Función `normalizeDesignId` que transforma un valor al formato requerido.
function normalizeDesignId(value, fallback) {
  if (value && typeof value === 'object') return value.id || fallback;
  return value || fallback;
}

// Función `getDesignNumber` que obtiene la información necesaria para el flujo.
function getDesignNumber(value, fallback = 1) {
  // Valor intermedio `match` utilizado por la lógica de este bloque.
  const match = String(value || '').match(/\d+/);
  return match ? Number(match[0]) : fallback;
}

// Función `waitForBrowserPaint` que encapsula una operación reutilizable de este módulo.
function waitForBrowserPaint() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });
}

// Función `waitForImages` que encapsula una operación reutilizable de este módulo.
function waitForImages() {
  return Promise.all(
    Array.from(document.images).map((img) => {
      if (img.complete) return Promise.resolve();
      return new Promise((resolve) => {
        img.addEventListener('load', resolve, { once: true });
        img.addEventListener('error', resolve, { once: true });
      });
    })
  );
}

/**
 * Página interna sin UI utilizada únicamente por Chromium para generar el PDF.
 * Los datos se entregan en memoria mediante un CustomEvent: nunca se incluyen
 * en la URL ni quedan disponibles como un endpoint público.
 */
export default function BoletaCanonicalRenderPage() {
  // Estado local `boleta` y su actualizador, usados para reflejar cambios en la interfaz.
  const [boleta, setBoleta] = useState(null);

  useEffect(() => {
    window.__BOLETA_RENDER_READY__ = false;
    window.__BOLETA_RENDER_ACCEPTING__ = true;

    // Función `receiveBoleta` que encapsula una operación reutilizable de este módulo.
    const receiveBoleta = (event) => {
      window.__BOLETA_RENDER_READY__ = false;
      setBoleta(event.detail || null);
    };

    window.addEventListener('wisby:render-boleta', receiveBoleta);
    return () => {
      window.removeEventListener('wisby:render-boleta', receiveBoleta);
      delete window.__BOLETA_RENDER_ACCEPTING__;
      delete window.__BOLETA_RENDER_READY__;
    };
  }, []);

  // Valor `renderProps` memoizado para recalcularlo solo cuando cambian sus dependencias.
  const renderProps = useMemo(() => {
    if (!boleta) return null;

    // Valor intermedio `orientation` utilizado por la lógica de este bloque.
    const orientation = boleta.orientation || 'vertical';
    // Valor intermedio `designId` utilizado por la lógica de este bloque.
    const designId = normalizeDesignId(boleta.designId, 'prem_design_1');
    // Valor intermedio `logoId` utilizado por la lógica de este bloque.
    const logoId = normalizeDesignId(boleta.logoId, 'logo1');
    // Valor intermedio `membreteId` utilizado por la lógica de este bloque.
    const membreteId = normalizeDesignId(boleta.membreteId, 'membrete1');
    // Valor intermedio `designNumber` utilizado por la lógica de este bloque.
    const designNumber = getDesignNumber(designId);
    // Valor intermedio `logoNumber` utilizado por la lógica de este bloque.
    const logoNumber = getDesignNumber(logoId);
    // Valor intermedio `membreteNumber` utilizado por la lógica de este bloque.
    const membreteNumber = getDesignNumber(membreteId);

    return {
      boletaData: {
        ...boleta,
        orientation,
        designId,
        logoId,
        membreteId,
        companyName: boleta.companyName || '',
        textFont: boleta.textFont || 'Montserrat',
        textSize: boleta.textSize || 24,
        sealColor: boleta.sealColor || 'grey',
      },
      designUrls: {
        marco: `${PUBLIC_URL}/designs/marcos/marco_${designNumber}.svg`,
        logo: `${PUBLIC_URL}/designs/logos/${logoNumber}_A.svg`,
        membrete: `${PUBLIC_URL}/designs/membretes/${membreteNumber}_A.svg`,
        textoLegal: `${PUBLIC_URL}/designs/texto.svg`,
      },
      orientation,
      cuponDesignNumber: boleta.cuponDesignNumber || 1,
      selectedLogoNumber: logoNumber,
      adminFields: boleta.adminFields,
      series: boleta.series,
      textoLegalOverride: boleta.textoLegalOverride,
      reversoOverride: boleta.reversoOverride,
      boletaCode: boleta.boletaCode ?? 0,
      // DocuSign debe usar exactamente la composición horizontal aprobada
      // en el modal administrativo (tipografías, ondas, escudo y columnas).
      adminEditorPreview: orientation === 'horizontal',
      // Los factores originales exceden ligeramente el alto del tabloide y
      // cortan el título. Este ajuste no afecta el área de cupones.
      boletaScaleMultiplier: orientation === 'horizontal' ? 0.88 : 0.9,
      // En el reverso horizontal se conserva la escala usada en la vista de
      // ActionsSection/impresión para no reducir el escudo junto con el frente.
      backBoletaScaleMultiplier: orientation === 'horizontal' ? 1 : 0.9,
    };
  }, [boleta]);

  useEffect(() => {
    if (!renderProps) return undefined;
    // Valor intermedio `cancelled` utilizado por la lógica de este bloque.
    let cancelled = false;

    // Función `markReady` que encapsula una operación reutilizable de este módulo.
    const markReady = async () => {
      await waitForBrowserPaint();
      await waitForImages();
      if (document.fonts?.ready) await document.fonts.ready;
      // CenteredSvg inserta los SVG después de fetch; este segundo ciclo deja
      // que React aplique esos cambios antes de que Chromium imprima.
      await new Promise((resolve) => setTimeout(resolve, 350));
      await waitForBrowserPaint();
      if (!cancelled) window.__BOLETA_RENDER_READY__ = true;
    };

    markReady();
    return () => { cancelled = true; };
  }, [renderProps]);

  if (!renderProps) return null;

  return (
    <main className="boleta-canonical-document">
      <style>{`
        @page { size: 17in 11in; margin: 0; }
        html, body, #root { margin: 0 !important; padding: 0 !important; width: 17in; background: white; }
        body { overflow: visible !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        /* Defensa contra las reglas globales de window.print de AdminSection. */
        body > #root { display: block !important; position: static !important; }
        .boleta-canonical-document { width: 17in; margin: 0; padding: 0; background: white; }
        .boleta-canonical-sheet {
          position: relative;
          width: 17in;
          height: 11in;
          margin: 0;
          overflow: hidden;
          background: white;
          page-break-after: always;
          break-after: page;
        }
        .boleta-canonical-sheet:last-child { page-break-after: auto; break-after: auto; }
        .boleta-canonical-document *, .boleta-canonical-document *::before, .boleta-canonical-document *::after {
          animation: none !important;
          transition: none !important;
        }
        /* Las ondas se ocultaban en las reglas históricas de impresión. */
        .boleta-canonical-document img[src$="/3.svg"],
        .boleta-canonical-document img[src$="/3_H.svg"],
        .boleta-canonical-document img[src$="/6.svg"] {
          display: block !important;
          opacity: 1 !important;
          visibility: visible !important;
        }
        .boleta-canonical-document .holographic-wave-hue {
          display: block !important;
          opacity: 0.82 !important;
          visibility: visible !important;
          mix-blend-mode: multiply !important;
          filter: hue-rotate(210deg) saturate(2.7) brightness(1.14) contrast(1.2) !important;
        }
      `}</style>
      <section className="boleta-canonical-sheet">
        <BoletaFinalPreview {...renderProps} showBack={false} />
      </section>
      <section className="boleta-canonical-sheet">
        <BoletaFinalPreview {...renderProps} showBack={true} />
      </section>
    </main>
  );
}
